import { plural } from "./plural";

// Unit label localization. Data stores Ukrainian unit strings; map to en/ru.
// Keys are normalized (lowercased, trailing dots/spaces stripped) Ukrainian units.
const UNIT_MAP = {
  "г":         { en: "g",       ru: "г" },
  "кг":        { en: "kg",      ru: "кг" },
  "мл":        { en: "ml",      ru: "мл" },
  "л":         { en: "l",       ru: "л" },
  "шт":        { en: "pcs",     ru: "шт" },
  "ст.л":      { en: "tbsp",    ru: "ст. л" },
  "ч.л":       { en: "tsp",     ru: "ч. л" },
  "склянка":   { en: "cup",     ru: "стакан" },
  "пучок":     { en: "bunch",   ru: "пучок" },
  "зубчик":    { en: "clove",   ru: "зубчик" },
  "зубчики":   { en: "cloves",  ru: "зубчика" },
  "головка":   { en: "head",    ru: "головка" },
  "щіпка":     { en: "pinch",   ru: "щепотка" },
  "скибочка":  { en: "slice",   ru: "ломтик" },
  "скибочки":  { en: "slices",  ru: "ломтика" },
  "за смаком": { en: "to taste", ru: "по вкусу" },
};

// Count units that change form by number (BUG-004). Canonical key = singular UA form.
// one/few/many match the Slavic 3-form rule in plural.js; en uses few/many = same plural.
const COUNT_UNIT_FORMS = {
  "зубчик":   { uk: { one: "зубчик",   few: "зубчики",   many: "зубчиків" },  ru: { one: "зубчик",  few: "зубчика",  many: "зубчиков" },  en: { one: "clove",  few: "cloves",  many: "cloves" } },
  "скибочка": { uk: { one: "скибочка", few: "скибочки",  many: "скибочок" },  ru: { one: "ломтик",  few: "ломтика",  many: "ломтиков" },  en: { one: "slice",  few: "slices",  many: "slices" } },
  "головка":  { uk: { one: "головка",  few: "головки",   many: "головок" },   ru: { one: "головка", few: "головки",  many: "головок" },   en: { one: "head",   few: "heads",   many: "heads" } },
  "пучок":    { uk: { one: "пучок",    few: "пучки",     many: "пучків" },    ru: { one: "пучок",   few: "пучка",    many: "пучков" },    en: { one: "bunch",  few: "bunches", many: "bunches" } },
  "склянка":  { uk: { one: "склянка",  few: "склянки",   many: "склянок" },   ru: { one: "стакан",  few: "стакана",  many: "стаканов" },   en: { one: "cup",    few: "cups",    many: "cups" } },
  "щіпка":    { uk: { one: "щіпка",    few: "щіпки",     many: "щіпок" },     ru: { one: "щепотка", few: "щепотки",  many: "щепоток" },    en: { one: "pinch",  few: "pinches", many: "pinches" } },
};

// Plural UA unit forms the AI may emit -> canonical singular key in COUNT_UNIT_FORMS.
const COUNT_UNIT_ALIASES = { "зубчики": "зубчик", "скибочки": "скибочка" };

function normalizeUnit(u) {
  return (u || "").toString().trim().toLowerCase().replace(/[.\s]+$/, "");
}

// Reverse map: any known unit form (ua / en / ru, singular or plural) -> canonical UA key.
// Lets us localize units that arrive from AI in English (e.g. "g", "pc", "cloves").
const UNIT_TO_UA = {};
for (const [ua, f] of Object.entries(UNIT_MAP)) {
  UNIT_TO_UA[ua] = ua;
  if (f.en) UNIT_TO_UA[f.en.toLowerCase()] = ua;
  if (f.ru) UNIT_TO_UA[f.ru.toLowerCase()] = ua;
}
Object.assign(UNIT_TO_UA, {
  "pc": "шт", "pcs": "шт", "piece": "шт", "pieces": "шт", "pce": "шт",
  "gram": "г", "grams": "г", "gr": "г",
  "kilogram": "кг", "kilograms": "кг",
  "milliliter": "мл", "milliliters": "мл", "millilitre": "мл",
  "liter": "л", "liters": "л", "litre": "л", "litres": "л",
  "clove": "зубчик", "cloves": "зубчик",
  "slice": "скибочка", "slices": "скибочка",
  "cup": "склянка", "cups": "склянка",
  "pinch": "щіпка", "pinches": "щіпка",
  "head": "головка", "heads": "головка",
  "bunch": "пучок", "bunches": "пучок",
  "tablespoon": "ст.л", "tablespoons": "ст.л", "tbsp": "ст.л",
  "teaspoon": "ч.л", "teaspoons": "ч.л", "tsp": "ч.л",
  "to taste": "за смаком",
});

function toUA(unit) {
  return UNIT_TO_UA[normalizeUnit(unit)] || null;
}

export function formatUnit(unit, lang) {
  if (!unit) return "";
  const uaKey = toUA(unit);                 // map en/ru/ua form -> canonical UA key
  if (lang === "uk" || lang === "ua") return uaKey || unit;
  const entry = UNIT_MAP[uaKey || normalizeUnit(unit)];
  if (!entry) return unit;                  // unknown unit -> show as-is
  return entry[lang] || entry.en || unit;
}

// Trim a trailing ".0" for display (BUG-002): "4.0"/"100.0"/4 -> "4"/"100"; "0.5" stays "0.5".
// BUG-003 (comma separator) is intentionally NOT done here -> decimal stays a dot.
export function formatAmount(amount, lang) {
  if (amount === null || amount === undefined || amount === "") return "";
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);  // non-numeric -> show as-is
  return String(n);                                // Number drops ".0"; keeps real fractions
}

// Pluralize a count unit by amount (BUG-004). Non-count units fall back to formatUnit (unchanged).
export function formatUnitPlural(unit, amount, lang) {
  if (!unit) return "";
  const norm = toUA(unit) || normalizeUnit(unit);
  const canonical = COUNT_UNIT_ALIASES[norm] || norm;
  const forms = COUNT_UNIT_FORMS[canonical];
  if (!forms) return formatUnit(unit, lang);       // non-count -> existing behavior
  const langForms = forms[lang] || forms.en;
  const n = Number(amount);
  if (!Number.isInteger(n)) return langForms.few;  // fractional amount -> few form
  return plural(lang, n, langForms);
}